import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ErrorFallbackScreen from './ErrorFallbackScreen';

// useRouteError() is not always an Error — data routers can surface a
// Response (throw json()/redirect()) or any thrown value. Normalize so
// Sentry gets a real exception with a meaningful message instead of a
// "Non-Error exception captured" event, while still ALWAYS reporting.
const toError = (routeError: unknown): Error => {
  if (routeError instanceof Error) return routeError;
  if (isRouteErrorResponse(routeError)) {
    return new Error(`Route error response ${routeError.status}: ${routeError.statusText}`);
  }
  return new Error(`Non-Error route error: ${String(routeError)}`);
};

/**
 * TELV1-489 — errorElement for the data router. React Router catches
 * route render errors with its own boundary BEFORE the class
 * ErrorBoundary wrapping RouterProvider can see them, so without this
 * element respondents got React Router's default stack-trace page and
 * Sentry never received the error (the screening-index crash shipped for
 * weeks with zero events). Reports once per error, then renders the same
 * branded recovery screen as ErrorBoundary.
 *
 * Deliberately NOT extracted into a package shared with dashboard and
 * super-admin (Release 3.5.0 audit follow-up evaluated and rejected it):
 * their fallbacks must stay import-free above app context (see #1654), so a
 * workspace import would re-add the crash surface they exist to avoid, and
 * the variants differ on purpose — this one renders translated
 * respondent-facing copy with the chat brand, the other two resolve their
 * own Tailwind theme tokens.
 */
function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(toError(error), { tags: { error_boundary: 'router' } });
  }, [error]);

  return <ErrorFallbackScreen onReload={() => window.location.reload()} />;
}

export default RouteErrorFallback;
